import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { api, unwrap, type Schemas } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { Button, LinkButton, Notice, TextField } from './ui.js';

type Fix = Schemas['FixContent'];

function CopyButton({ value }: { value: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="soft"
      size="sm"
      onClick={() => {
        void navigator.clipboard
          ?.writeText(value)
          .then(() => setCopied(true))
          .catch(() => setCopied(false));
      }}
    >
      {copied ? t('fixes.copied') : t('fixes.copy')}
    </Button>
  );
}

/** A ready-made fix (T-72): what, where, steps, the values to copy, page by page, warnings. */
export function FixContentView({ fix }: { fix: Fix }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3 wrap-anywhere">
      <p>{fix.summary}</p>
      <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[auto_1fr]">
        <dt className="font-semibold">{t('fixes.where')}</dt>
        <dd>{fix.where}</dd>
        <dt className="font-semibold">{t('fixes.who')}</dt>
        <dd>{t(`fixes.needs.${fix.needs}`)}</dd>
        <dt className="font-semibold">{t('fixes.time')}</dt>
        <dd>{t('fixes.minutes', { count: fix.minutes })}</dd>
      </dl>
      {fix.steps.length > 0 ? (
        <div>
          <h4 className="font-semibold">{t('fixes.steps')}</h4>
          <ol className="ml-5 list-decimal">
            {fix.steps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </div>
      ) : null}
      {fix.values.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {fix.values.map((v, i) => (
            <li key={i} className="rounded-md border border-slate-200 bg-slate-50 p-2">
              <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold">{v.label}</span>
                <CopyButton value={v.value} />
              </div>
              {v.kind === 'text' ? (
                <p className="whitespace-pre-wrap">{v.value}</p>
              ) : (
                <pre className="overflow-x-auto text-sm">
                  <code>{v.value}</code>
                </pre>
              )}
            </li>
          ))}
        </ul>
      ) : null}
      {fix.table ? (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr>
                {fix.table.columns.map((c, i) => (
                  <th key={i} className="border-b border-slate-300 p-2">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fix.table.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c} className="border-b border-slate-200 p-2 align-top">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {fix.warnings.length > 0 ? (
        <Notice kind="warning">
          <ul className="ml-5 list-disc">
            {fix.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </Notice>
      ) : null}
    </div>
  );
}

function ShareForm({ siteId, ruleCode }: { siteId: string; ruleCode: string }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const messageId = useId();
  const share = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/sites/{siteId}/fix-shares', {
          params: { path: { siteId } },
          body: {
            ruleCodes: [ruleCode],
            email: email.trim(),
            ...(message.trim() ? { message: message.trim() } : {}),
          },
        }),
      ),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    share.mutate();
  };
  if (share.isSuccess)
    return <Notice kind="success">{t('fixes.shareSent', { email: share.data.email })}</Notice>;
  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <p className="text-sm text-slate-700">{t('fixes.shareHint')}</p>
      <TextField
        label={t('fixes.shareEmail')}
        type="email"
        required
        maxLength={254}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <div className="flex flex-col gap-1">
        <label htmlFor={messageId} className="font-semibold">
          {t('fixes.shareMessage')}{' '}
          <span className="font-normal text-slate-600">({t('app.optional')})</span>
        </label>
        <textarea
          id={messageId}
          value={message}
          maxLength={1000}
          rows={3}
          onChange={(e) => setMessage(e.target.value)}
          className="rounded-md border border-slate-400 bg-white px-3 py-2 text-base"
        />
      </div>
      {share.isError ? <Notice kind="error">{errorMessage(share.error, t)}</Notice> : null}
      <div>
        <Button type="submit" busy={share.isPending}>
          {t('fixes.shareSend')}
        </Button>
      </div>
    </form>
  );
}

/** "Fertige Lösung" of a problem group (T-72): make it, copy the values, check, share. */
export function FixPanel({ siteId, ruleCode }: { siteId: string; ruleCode: string }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const key = ['site', siteId, 'fix', ruleCode];
  const [sharing, setSharing] = useState(false);
  const state = useQuery({
    queryKey: key,
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/fixes/{ruleCode}', { params: { path: { siteId, ruleCode } } }),
      ),
  });
  const make = useMutation({
    mutationFn: (regenerate: boolean) =>
      unwrap(
        api.POST('/sites/{siteId}/fixes/{ruleCode}', {
          params: { path: { siteId, ruleCode } },
          body: { regenerate },
        }),
      ),
    onSuccess: (data) => client.setQueryData(key, data),
  });
  const recheck = useMutation({
    mutationFn: () => unwrap(api.POST('/sites/{siteId}/recheck', { params: { path: { siteId } } })),
  });
  const s = state.data;
  if (!s || s.ownerOnly || s.openProblems === 0 || (!s.available && !s.locked)) return null;

  if (s.locked)
    return (
      <Notice kind="info">
        <p className="font-semibold">{t('fixes.title')}</p>
        <p>{t('fixes.locked')}</p>
        <LinkButton to="/billing" variant="secondary" size="sm" className="mt-2">
          {t('fixes.upgrade')}
        </LinkButton>
      </Notice>
    );

  const pkg = s.package;
  const busy = make.isPending;
  return (
    <section className="rounded-md border border-blue-200 bg-blue-50/40 p-3">
      <h3 className="mb-2 font-semibold">{t('fixes.title')}</h3>
      {!pkg ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-slate-700">
            {s.kind === 'ai'
              ? t('fixes.introAi', { used: s.quota.used, limit: s.quota.limit })
              : t('fixes.introTemplate')}
          </p>
          <div>
            <Button busy={busy} onClick={() => make.mutate(false)}>
              {t('fixes.create')}
            </Button>
          </div>
          {busy && s.kind === 'ai' ? (
            <p className="text-sm text-slate-700">{t('fixes.working')}</p>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {s.stale ? (
            <Notice kind="info">
              <p>{t('fixes.stale')}</p>
              <Button
                variant="secondary"
                size="sm"
                className="mt-2"
                busy={busy}
                onClick={() => make.mutate(false)}
              >
                {t('fixes.update')}
              </Button>
            </Notice>
          ) : null}
          <FixContentView fix={pkg.content} />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              busy={recheck.isPending}
              onClick={() => recheck.mutate()}
            >
              {t('fixes.recheck')}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setSharing(!sharing)}>
              {t('fixes.share')}
            </Button>
            {s.regenerationsLeft > 0 && !s.stale ? (
              <Button variant="soft" size="sm" busy={busy} onClick={() => make.mutate(true)}>
                {t('fixes.regenerate', { count: s.regenerationsLeft })}
              </Button>
            ) : null}
          </div>
          {recheck.isSuccess ? <Notice kind="success">{t('fixes.rechecking')}</Notice> : null}
          {recheck.isError ? <Notice kind="error">{errorMessage(recheck.error, t)}</Notice> : null}
          {s.kind === 'ai' ? (
            <p className="text-sm text-slate-700">
              {t('fixes.quota', { used: s.quota.used, limit: s.quota.limit })}
            </p>
          ) : null}
          {sharing ? <ShareForm siteId={siteId} ruleCode={ruleCode} /> : null}
        </div>
      )}
      {make.isError ? (
        <div className="mt-2">
          <Notice kind="error">{errorMessage(make.error, t)}</Notice>
        </div>
      ) : null}
    </section>
  );
}
